import { useRef, useState } from 'react';
import { heicTo, isHeic } from 'heic-to';
import { toast } from 'react-hot-toast';
import { CaretRightIcon, ImageIcon, WarningIcon, XIcon } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../../components/ui/drawer';
import { Button } from '../../../components/ui/button';
import ModalCloseButton from '../../../components/generic/ModalCloseButton';
import ImageCropperModal from './ImageCropperModal';
import { useIsTablet } from '../../../lib/useIsTablet';

const MAX_IMAGES = 3;
const MAX_IMAGE_SIZE_BYTES = 8 * 1024 * 1024;

// Browsers can't decode HEIC/HEIF in <img>/canvas, so convert it to a viewable
// format up front — the user then crops/rotates normally, and the final export
// step converts whatever they end up with to webp regardless of source format.
const toEditableBlob = async (file) => {
  if (!(await isHeic(file))) return file;
  return heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
};

// images: [{ id, blob, previewUrl }]
const AddEditSolutionModal = ({
  open,
  isEdit,
  initialImages = [],
  showReverifyWarning,
  onClose,
  onSubmit,
  isSubmitting,
  onViewGuidelines,
}) => {
  const [images, setImages] = useState(initialImages);
  const [pendingFileSrc, setPendingFileSrc] = useState(null);
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const fileInputRef = useRef(null);
  const isTablet = useIsTablet();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error('Photo is too large. Please upload an image under 8MB.');
      return;
    }

    setIsPreparingImage(true);
    try {
      const editableBlob = await toEditableBlob(file);
      setPendingFileSrc(URL.createObjectURL(editableBlob));
    } catch (error) {
      // The decoder couldn't parse this file at all — surface it instead of a
      // silent blank crop (the raw file won't render in <img>/canvas either).
      toast.error(
        (await isHeic(file).catch(() => false))
          ? "Couldn't open this HEIC photo — try a different photo, or export it as JPEG/PNG first."
          : "Couldn't open this image. Please try a different file.",
      );
    } finally {
      setIsPreparingImage(false);
    }
  };

  const handleCropConfirm = (blob) => {
    const previewUrl = URL.createObjectURL(blob);
    setImages((prev) => [...prev, { id: `${Date.now()}-${prev.length}`, blob, previewUrl }]);
    setPendingFileSrc(null);
  };

  const handleRemoveImage = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleClose = () => {
    setImages(initialImages);
    onClose();
  };

  const handlePublish = () => {
    if (images.length === 0) return;
    onSubmit(images.map((img) => img.blob));
  };

  const renderBody = () => (
    <div className="flex flex-col gap-4">
      {showReverifyWarning && (
        <div className="flex items-center gap-2 border border-dashed border-[var(--q3-yellow-normal)] bg-[var(--q3-yellow-light)] rounded-lg px-4 py-3 text-xs md:text-sm text-[var(--q3-yellow-dark)]">
          <WarningIcon size={18} />
          Your solution will be reverified
        </div>
      )}

      <div className="border border-[var(--q3-stroke-light)] rounded-xl p-4">
        <div className="flex flex-row items-center justify-between mb-3">
          <span className="text-sm text-[var(--q3-neutral-default)]">Minimum 1 image is required</span>
          <span className="text-sm text-[var(--q3-neutral-default)]">
            {images.length}/{MAX_IMAGES}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {images.map((img, index) => (
            <div key={img.id} className="flex flex-col relative">
              <div className="cursor-pointer relative aspect-square rounded-md overflow-hidden bg-[var(--q3-surface-dim)]">
                <span className="absolute top-1 left-1 bg-white text-black text-[12px] font-semibold px-1.5 rounded">
                  {index + 1}
                </span>
                <img src={img.previewUrl} alt={`solution-${index + 1}`} className="w-full h-full object-cover" />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveImage(img.id)}
                className="cursor-pointer absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center justify-center gap-1 w-[88px] text-sm py-1 bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] rounded"
              >
                <XIcon size={14} />
              </button>
            </div>
          ))}

          {images.length < MAX_IMAGES && (
            <button
              type="button"
              disabled={isPreparingImage}
              onClick={() => fileInputRef.current?.click()}
              className="cursor-pointer flex flex-col items-center justify-center gap-4 aspect-square rounded-md border border-dashed border-[var(--q3-blue-normal)] bg-[var(--q3-blue-light)] text-[var(--q3-blue-normal)] disabled:opacity-60 disabled:cursor-wait"
            >
              <ImageIcon size={22} />
              <span className="text-sm">{isPreparingImage ? 'Preparing...' : 'Add Image'}</span>
            </button>
          )}
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      </div>

      <Button
        className="w-full rounded-full bg-[var(--q3-blue-normal)] hover:scale-100 hover:bg-[var(--q3-blue-dark)] duration-200"
        disabled={images.length === 0 || isSubmitting}
        onClick={handlePublish}
      >
        {isSubmitting ? 'Publishing...' : 'Publish Community Solution'}
      </Button>

      <button
        type="button"
        onClick={onViewGuidelines}
        className="cursor-pointer text-sm text-center text-[var(--q3-neutral-default)] flex flex-row items-center justify-center"
      >
        View guidelines for a faster Approval
        <CaretRightIcon size={20} />
      </button>
    </div>
  );

  const title = isEdit ? 'Edit your solution' : 'Add your solution';
  const isOpen = open && !pendingFileSrc;

  return (
    <>
      {isTablet ? (
        <Dialog open={isOpen} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
          <DialogContent className="max-w-lg" showCloseButton={false}>
            <ModalCloseButton onClick={handleClose} />
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            {renderBody()}
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={isOpen} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
          <DrawerContent showCloseButton={false} className="px-4 pb-6 max-h-[90vh]">
            <ModalCloseButton onClick={handleClose} />
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
            </DrawerHeader>
            <div className="overflow-y-auto">{renderBody()}</div>
          </DrawerContent>
        </Drawer>
      )}

      {pendingFileSrc && (
        <ImageCropperModal
          imageSrc={pendingFileSrc}
          onCancel={() => setPendingFileSrc(null)}
          onConfirm={handleCropConfirm}
        />
      )}
    </>
  );
};

export default AddEditSolutionModal;
